"use client"

import React, { useState, useContext, useEffect, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material"
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"

import dayjs from "dayjs"

import { ErpContext } from "../../context/ErpContext.js"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"

const TransaccionModal = ({
  open,
  onClose,
  fechaTransaccion,
  onTransactionAdded,
  modoDevolucionProveedor = false,
  idEntidadProveedor = null,
  idTransaccionParaRevertir = null,
  montoSugerido = null,
  tituloPersonalizado = null,
}) => {
  const {
    getTipoTransaccion,
    tipoTransaccion,
    getTipoMedioDePago,
    tipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    postTransaccion,
    postTransferenciaInterna,
    revertirTransaccionProveedor,
    user,
    postCrearPago,
    postTransaccionPago,
    getFechaHoraActualClient,
    getUbicacion,
    ubicaciones,
    formatoNumericoGlobal,
    getParametrosGlobales,
  } = useContext(ErpContext)

  const [formData, setFormData] = useState({
    tipoTransaccion: null,
    tipoMedioDePago: null,
    medioDePago: null,
    ubicacion: null,
    ubicacionDestino: null, // Para transferencias internas
    monto: "", // Valor numérico interno (number o "")
    descripcion: "",
  })
  const [montoDisplay, setMontoDisplay] = useState("") // Valor formateado para mostrar
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [fechaTrans, setFechaTrans] = useState(fechaTransaccion);
  const [ubicacionDefaultCargada, setUbicacionDefaultCargada] = useState(false)
  const [parametrosGlobalesLocal, setParametrosGlobalesLocal] = useState(null)

  // Obtener configuración de locale (memoizado para evitar recálculos)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  useEffect(() => {
    if (open) {
      // Cargar datos necesarios cuando se abre el modal
      fetchInitialData()
      cargarParametrosGlobales()
      // Modo devolución proveedor: precargar monto sugerido
      if (modoDevolucionProveedor && montoSugerido != null && montoSugerido > 0) {
        setFormData((prev) => ({ ...prev, monto: montoSugerido }))
        setMontoDisplay(formatInputWithSeparators(montoSugerido, getLocaleConfig(formatoNumericoGlobal || null).locale))
      }
    }
  }, [open])

  // Modo devolución: preseleccionar tipo Ingreso cuando carguen los tipos
  useEffect(() => {
    if (open && modoDevolucionProveedor && tipoTransaccion?.length) {
      const tipoIngreso = tipoTransaccion.find(
        (t) => t.verEnCaja === true && t.verEnColumna === "Ingreso"
      )
      if (tipoIngreso) {
        setFormData((prev) => ({ ...prev, tipoTransaccion: tipoIngreso }))
      }
    }
  }, [open, modoDevolucionProveedor, tipoTransaccion])

  const cargarParametrosGlobales = async () => {
    try {
      const parametros = await getParametrosGlobales(['usaTransferenciaInternaCaja'])
      setParametrosGlobalesLocal(parametros)
    } catch (error) {
      console.error("Error cargando parámetros globales:", error)
    }
  }

  useEffect(() => {
    if (fechaTransaccion) {
      setFechaTrans(dayjs(fechaTransaccion))
    }
  }, [fechaTransaccion])

  useEffect(() => {
    if (formData.tipoMedioDePago) {
      getMedioDePagoIdTipoEspecifico(formData.tipoMedioDePago)
    }
  }, [formData.tipoMedioDePago])

  // Sincronizar montoDisplay con formData.monto cuando cambia desde fuera
  useEffect(() => {
    if (formData.monto === "" || formData.monto == null) {
      setMontoDisplay("")
      return
    }

    const locale = localeConfig.locale
    const decimalSeparator = localeConfig.decimalSeparator

    if (typeof formData.monto === "number") {
      let formatted = formatInputWithSeparators(formData.monto, locale)
      setMontoDisplay(formatted)
    } else if (typeof formData.monto === "string") {
      const parsed = parseLocalizedNumber(formData.monto, locale)
      if (parsed !== null) {
        let formatted = formatInputWithSeparators(parsed, locale)
        if (formData.monto.endsWith(decimalSeparator) && !formatted.endsWith(decimalSeparator)) {
          formatted += decimalSeparator
        }
        setMontoDisplay(formatted)
      }
    }
  }, [formData.monto, localeConfig])

  const fetchInitialData = async () => {
    await getTipoTransaccion()
    await getTipoMedioDePago()
    await getUbicacion()
  }

  // Aplicar ubicación por defecto cuando se cargan las ubicaciones
  useEffect(() => {
    if (open && ubicaciones && ubicaciones.length > 0 && !ubicacionDefaultCargada) {
      const cargarUbicacionDefault = async () => {
        try {
          const parametros = await getParametrosGlobales(['idUbicacionDefaultTransaccionCaja'])
          const idUbicacionDefault = parametros?.idUbicacionDefaultTransaccionCaja
          
          if (idUbicacionDefault) {
            const ubicacionDefault = ubicaciones.find(u => u.id === parseInt(idUbicacionDefault))
            if (ubicacionDefault && ubicacionDefault.esDestino === false) {
              setFormData((prev) => ({ ...prev, ubicacion: ubicacionDefault }))
              setUbicacionDefaultCargada(true)
            }
          }
        } catch (error) {
          console.error("Error cargando ubicación por defecto:", error)
        }
      }
      
      cargarUbicacionDefault()
    }
    
    // Resetear flag cuando se cierra el modal
    if (!open) {
      setUbicacionDefaultCargada(false)
    }
  }, [open, ubicaciones, ubicacionDefaultCargada, getParametrosGlobales])

  // Filtrar solo tipos de transacción Ingreso y Egreso + agregar opción virtual de Transferencia Interna
  // En modo devolución proveedor: solo Ingreso, bloqueado
  const tiposTransaccionFiltrados = useMemo(() => {
    if (modoDevolucionProveedor) {
      return (tipoTransaccion || []).filter(t => 
        t.verEnCaja === true && t.verEnColumna === "Ingreso"
      )
    }
    const tiposFiltrados = (tipoTransaccion || []).filter(t => 
      t.verEnCaja === true && 
      (t.verEnColumna === "Ingreso" || t.verEnColumna === "Egreso")
    )
    
    // Agregar opción virtual de Transferencia Interna si el parámetro está activo
    if (parametrosGlobalesLocal?.usaTransferenciaInternaCaja === "1") {
      tiposFiltrados.push({
        id: "transferencia-interna",
        descripcion: "Transferencia Interna",
        verEnCaja: true,
        verEnColumna: null
      })
    }
    
    return tiposFiltrados
  }, [tipoTransaccion, parametrosGlobalesLocal, modoDevolucionProveedor])

  // Filtrar ubicaciones que NO son destinos (esDestino = false)
  const ubicacionesFiltradas = useMemo(() => {
    return (ubicaciones || []).filter(u => u.esDestino === false)
  }, [ubicaciones])

  // Filtrar ubicaciones destino (excluir la ubicación origen seleccionada)
  const ubicacionesDestinoFiltradas = useMemo(() => {
    return (ubicaciones || []).filter(u => 
      u.esDestino === false && 
      u.id !== formData.ubicacion?.id
    )
  }, [ubicaciones, formData.ubicacion])

  // Detectar si es transferencia interna
  const esTransferenciaInterna = formData.tipoTransaccion?.id === "transferencia-interna"

  const handleSubmit = async () => {
    setLoading(true)
    setError("")

    try {
      // Validar que el monto sea un número válido
      const montoValue = typeof formData.monto === "number" 
        ? formData.monto 
        : parseLocalizedNumber(formData.monto, localeConfig.locale)
      
      if (montoValue === null || montoValue <= 0) {
        setError("El monto debe ser un número mayor a 0")
        setLoading(false)
        return
      }

      // Validaciones específicas para transferencia interna
      if (esTransferenciaInterna) {
        if (!formData.ubicacionDestino) {
          setError("Debe seleccionar una ubicación destino")
          setLoading(false)
          return
        }
        if (formData.ubicacion?.id === formData.ubicacionDestino?.id) {
          setError("La ubicación origen y destino no pueden ser la misma")
          setLoading(false)
          return
        }
      }
      
      // Construir fecha con la hora actual: tomar la fecha del DatePicker pero con la hora actual
      const fechaConHoraActual = dayjs(fechaTrans).hour(dayjs().hour()).minute(dayjs().minute()).second(dayjs().second())
      const {fechaServidor, zona} = getFechaHoraActualClient(fechaConHoraActual)

      // Si es transferencia interna, usar función específica
      if (esTransferenciaInterna) {
        await postTransferenciaInterna(
          montoValue,
          formData.ubicacion?.id,
          formData.ubicacionDestino?.id,
          formData.medioDePago?.id,
          formData.descripcion || "Transferencia interna",
          user.id,
          1, // idNegocio
          fechaServidor,
        )
      } else {
        // Modo devolución: primero la reversión, luego el ingreso
        if (modoDevolucionProveedor && idTransaccionParaRevertir) {
          await revertirTransaccionProveedor(idTransaccionParaRevertir, "devolucion", montoValue)
        }

        // Crear transacción (ingreso)
        const idEntidadFinal = modoDevolucionProveedor && idEntidadProveedor ? idEntidadProveedor : null
        const transaccionResponse = await postTransaccion(
          montoValue,
          formData.tipoTransaccion?.id,
          idEntidadFinal,
          null,
          formData.descripcion,
          user.id,
          formData.ubicacion?.id || 1,
          1,
          0,
          0,
          null,
          fechaServidor,
        )

        const idTransaccion = transaccionResponse.data.id
        const transaccionCrearPago = await postCrearPago(1, 1, formData.medioDePago?.id, montoValue)
        const idTransaccionCrearPago = transaccionCrearPago.data.id
        await postTransaccionPago(idTransaccion, idTransaccionCrearPago)
      }

      // Resetear formulario
      setFormData({
        tipoTransaccion: null,
        tipoMedioDePago: null,
        medioDePago: null,
        ubicacion: null,
        ubicacionDestino: null,
        monto: "",
        descripcion: "",
      })
      setMontoDisplay("")
      setFechaTrans(dayjs())

      // Cerrar modal y refrescar datos
      onTransactionAdded()
      onClose()
    } catch (err) {
      setError("Error al crear la transacción")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setFormData({
        tipoTransaccion: null,
        tipoMedioDePago: null,
        medioDePago: null,
        ubicacion: null,
        ubicacionDestino: null,
        monto: "",
        descripcion: "",
      })
      setMontoDisplay("")
      setFechaTrans(dayjs())
      setError("")
      onClose()
    }
  }

  const isFormValid = 
    formData.tipoTransaccion && 
    formData.medioDePago && 
    formData.monto && 
    formData.ubicacion &&
    (!esTransferenciaInterna || formData.ubicacionDestino) // Si es transferencia, validar destino

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#5b5b5f",
          color: "white",
          textAlign: "center",
          fontWeight: "bold",
        }}
      >
        {tituloPersonalizado || "Agregar Nueva Transacción"}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          {/* 1. Fecha */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Fecha"
              format="DD/MM/YYYY"
              disableFuture
              maxDate={dayjs()}
              size="small"
              value={fechaTrans}
              onChange={(newValue) => setFechaTrans(newValue)}
              slotProps={{
                textField: { size: "small", inputProps: { maxLength: 10 } },
              }}
              sx={{ width: "100%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ width: "100%" }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>

          {/* 2. Tipo de Transacción (solo Ingreso/Egreso; bloqueado en modo devolución) */}
          <Autocomplete
            options={tiposTransaccionFiltrados}
            getOptionLabel={(option) => option.descripcion || ""}
            value={formData.tipoTransaccion}
            size="small"
            onChange={(event, newValue) => {
              setFormData((prev) => ({ ...prev, tipoTransaccion: newValue }))
            }}
            disabled={modoDevolucionProveedor}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Transacción *"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* 3. Ubicación (Origen si es transferencia interna) */}
          <Autocomplete
            options={ubicacionesFiltradas}
            getOptionLabel={(option) => option.descripcion || ""}
            value={formData.ubicacion}
            size="small"
            onChange={(event, newValue) => {
              setFormData((prev) => ({ 
                ...prev, 
                ubicacion: newValue,
                // Limpiar ubicación destino si cambia origen
                ubicacionDestino: esTransferenciaInterna ? null : prev.ubicacionDestino
              }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={esTransferenciaInterna ? "Ubicación Origen *" : "Ubicación *"}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* 3b. Ubicación Destino (solo para transferencia interna) */}
          {esTransferenciaInterna && (
            <Autocomplete
              options={ubicacionesDestinoFiltradas}
              getOptionLabel={(option) => option.descripcion || ""}
              value={formData.ubicacionDestino}
              size="small"
              onChange={(event, newValue) => {
                setFormData((prev) => ({ ...prev, ubicacionDestino: newValue }))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ubicación Destino *"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          )}

          {/* 4. Tipo de Medio de Pago */}
          <Autocomplete
            options={(tipoMedioDePago || []).filter(t => t.id === 1 || t.id === 2|| t.id === 3|| t.id === 4)}
            getOptionLabel={(option) => option.descripcion || ""}
            value={formData.tipoMedioDePago}
            size="small"
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                tipoMedioDePago: newValue,
                medioDePago: null, // Reset medio de pago específico
              }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Medio de Pago *"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* 5. Medio de Pago Específico */}
          <Autocomplete
            options={medioDePagoIdTipoEspecifico || []}
            getOptionLabel={(option) => option.descripcion || ""}
            value={formData.medioDePago}
            size="small"
            onChange={(event, newValue) => {
              setFormData((prev) => ({ ...prev, medioDePago: newValue }))
            }}
            disabled={!formData.tipoMedioDePago}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Medio de Pago Específico *"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PointOfSaleIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* 6. Monto */}
          <TextField
            label="Monto *"
            type="text"
            variant="outlined"
            size="small"
            value={montoDisplay}
            onChange={(e) => {
              const input = e.target
              const inputValue = input.value
              const cursorPosition = input.selectionStart
              const { locale, decimalSeparator, thousandSeparator } = localeConfig

              if (inputValue === "") {
                setMontoDisplay("")
                setFormData((prev) => ({ ...prev, monto: "" }))
                return
              }

              const parsedValue = parseLocalizedNumber(inputValue, locale)

              if (parsedValue === null && inputValue.trim() !== "") {
                return
              }

              let formattedValue = parsedValue !== null
                ? formatInputWithSeparators(parsedValue, locale)
                : ""

              const hasDecimalSeparator = inputValue.includes(decimalSeparator)
              const endsWithDecimal = inputValue.endsWith(decimalSeparator)
              const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

              if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                formattedValue += decimalSeparator
              } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                const integerPart = formattedValue.split(decimalSeparator)[0]
                formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
              }

              setMontoDisplay(formattedValue)
              setFormData((prev) => ({ ...prev, monto: parsedValue ?? "" }))

              // Calcular nueva posición del cursor
              setTimeout(() => {
                const beforeCursor = inputValue.substring(0, cursorPosition)
                const numericCharsBefore = beforeCursor.replace(new RegExp(`\\${thousandSeparator}`, "g"), "").length
                
                let newCursorPosition = formattedValue.length
                
                if (numericCharsBefore > 0) {
                  let numericCount = 0
                  for (let i = 0; i < formattedValue.length; i++) {
                    const char = formattedValue[i]
                    if (char !== thousandSeparator) {
                      numericCount++
                      if (numericCount >= numericCharsBefore) {
                        newCursorPosition = i + 1
                        break
                      }
                    }
                  }
                }
                
                input.setSelectionRange(newCursorPosition, newCursorPosition)
              }, 0)
            }}
            onBlur={(e) => {
              const { locale } = localeConfig
              const parsed = parseLocalizedNumber(e.target.value, locale)
              if (parsed !== null && parsed > 0) {
                const formatted = formatInputWithSeparators(parsed, locale)
                setMontoDisplay(formatted)
                setFormData((prev) => ({ ...prev, monto: parsed }))
              } else if (e.target.value === "") {
                setMontoDisplay("")
                setFormData((prev) => ({ ...prev, monto: "" }))
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon sx={{ color: "rgb(244, 177, 14)" }} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: "decimal",
              autoComplete: "off",
            }}
          />

          {/* 7. Descripción */}
          <TextField
            label="Descripción (Opcional)"
            variant="outlined"
            multiline
            size="small"
            rows={3}
            value={formData.descripcion}
            onChange={(e) => setFormData((prev) => ({ ...prev, descripcion: e.target.value }))}
            placeholder="Ingrese una descripción para la transacción..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderColor: "#5b5b5f",
            color: "#5b5b5f",
            "&:hover": {
              borderColor: "rgb(244, 177, 14)",
              color: "rgb(244, 177, 14)",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading}
          sx={{
            backgroundColor: "#5b5b5f",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(244, 177, 14, 0.8)",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
              Guardando...
            </>
          ) : (
            "Agregar Transacción"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TransaccionModal
